<template>
    <div>
        <el-form size="large" :model="user" v-loading="!loaded" inline label-position="top" :rules="userRules" ref="mainForm">
            <el-form-item prop="enabled" v-if="canEditUserEnable" label="Account enabled?">
                <el-switch v-model="user.enabled" active-text="Enabled" inactive-text="Disabled" inline-prompt class="user-enable-switch" />
            </el-form-item>
            <el-form-item prop="email" label="Email address" style="flex-basis: 100%">
                <el-input v-model="user.email" placeholder="E-Mail Address" :disabled="!fieldEditable('email')"><template #prepend>E-Mail Address</template></el-input>
            </el-form-item>
            <el-form-item prop="first_name" label="First name">
                <el-input v-model="user.first_name" placeholder="First Name" :disabled="!fieldEditable('first_name')"><template #prepend>First Name</template></el-input>
            </el-form-item>
            <el-form-item prop="last_name" label="Last name">
                <el-input v-model="user.last_name" placeholder="Last Name" :disabled="!fieldEditable('last_name')"><template #prepend>Last Name</template></el-input>
            </el-form-item>
            <el-form-item prop="roles" v-if="canEditRoles && enableRoles" label="Roles" style="flex-basis: 100%">
                <el-select
                    v-model="user.roles"
                    placeholder="Roles"
                    multiple
                    filterable
                    value-key="id"
                >
                    <el-option v-for="role in roles" :key="role.id" :value="role" :label="role.name" />
                </el-select>
            </el-form-item>
        </el-form>
        <el-form label-position="top" size="large" :model="user" v-loading="!loaded" inline>
            <el-form-item>
                <el-button type="primary" @click="$emit('save', user)">{{saveLabel}}</el-button>
                <el-button v-if="cancel" type="danger" plain @click="$emit('cancel')">{{cancelLabel}}</el-button>
            </el-form-item>
        </el-form>
    </div>
</template>

<script>
import api from '@/lib/api'
import {mapGetters} from 'vuex'

export default {
    name: 'EditUserForm',
    props: {
        modelValue: { type: Object, default: undefined },
        id: { type: String, default: undefined },
        saveLabel: { type: String, default: 'Save' },
        cancelLabel: { type: String, default: 'Cancel' },
        cancel: { type: Boolean, default: true },
        rolesFetch: {
            type: Function,
            default: api.user.getUsersRoles
        },
        enableRoles: { type: Boolean, default: false },
    },
    emits: ['update:modelValue', 'save', 'cancel', 'user'],
    watch: {
        userId() {
            this.$refs.mainForm.resetFields()
            this.fetchUser()
        }
    },
    data: () => {
        return {
            loaded: false,
            editableFields: [],
            requiredFields: [],
            roles: [],
            userRules: {
                email: { type: 'email', required: true, message: 'A valid email address is required' },
                first_name: { required: true, message: 'First name is mandatory' },
                last_name: { required: true, message: 'Last name is mandatory' }
            }
        }
    },
    computed: {
        ...mapGetters('auth', ['userinfo']),

        user: {
            get () { return this.modelValue || {} },
            set(value) { this.$emit('update:modelValue', value) }
        },

        userId () {
            return this.user?.id
        },

        canEditRoles () {
            if (!this.id) {
                return true
            }

            return this.user?.meta?.fields.roles.edit
        },

        canEditUserEnable () {
            // Do not allow users to disable themselves LOL
            // ID can be passed either via v-model or via 'id' prop
            const sameUser = (this.user?.id === this.userinfo.id) || (this.id === this.userinfo.id)
            return (!this.id || this.user?.meta?.fields?.enabled?.edit) && !sameUser
        },

        rolesOptions () {
            return this.roles.map(role => {
                return {
                    value: role,
                    label: role.name
                }
            })
        }
    },
    methods: {
        fieldRequired (field) {
            return this.requiredFields.indexOf(field) !== -1
        },
        fieldEditable (field) {
            return this.editableFields.indexOf(field) !== -1
        },
        async fetchRoles () {
            if (!this.enableRoles) {
                return
            }

            if (!this.roles.length) {
                this.rolesFetch().then((response) => { this.roles = response.data })
            }
        },
        fetchUser () {
            if (!this.userId) {
                api.user.getUserCreate().then(response => {
                    this.editableFields = Object.keys(response.data)
                    this.requiredFields = this.editableFields.filter(field => {
                        return response.data[field].required
                    })

                    this.fetchRoles()
                    this.loaded = true
                }).catch(() => {
                    this.editableFields = []
                })

                return
            }

            api.user.getUserEdit(this.userId).then(response => {
                this.editableFields = Object.keys(response.data)
                this.requiredFields = this.editableFields.filter(field => {
                    return response.data[field].required
                })
            }).catch(() => {
                this.editableFields = []
                this.requiredFields = []
            })

            api.user.getUser(this.userId).then(response => {
                this.user = response.data
                this.loaded = true

                this.$emit('user', this.user)

                this.fetchRoles ()
            }).catch(error => {
                const err = error?.response?.data?.message || error.message

                this.$notify({
                    title: 'Error',
                    message: `Could not retrieve user information: ${err}`,
                    type: 'error'
                })
            })
        },
        save () {
            this.$emit('save', this.user)
        }
    },
    async mounted () {
        this.fetchUser()
    }
}
</script>

<style scoped>
.user-enable-switch {
    --el-switch-on-color: var(--el-color-success);
    --el-switch-off-color: var(--el-color-danger);
}
</style>